import React, { useState, useEffect } from 'react';
import { Search, Bookmark, ExternalLink, Filter, X } from 'lucide-react';
import { getAllComments, updateComment } from '../services/api';

interface Comment {
    id: number;
    commentId: string;
    commentBody: string;
    postedAt: string;
    aiSentiment: string;
    aiSentimentEdited?: string;
    isBookmarked: boolean;
    post: {
        postId: string;
        postBody: string | null;
        postDescription: string | null;
        imageUrl: string | null;
        videoUrl: string | null;
        platform: string;
        permalink: string | null;
        author: {
            username: string;
            name: string;
        };
    };
}

const CommentListPage: React.FC = () => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [platformFilter, setPlatformFilter] = useState<string>('');
    const [sentimentFilter, setSentimentFilter] = useState<string>('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(20);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [tempPlatform, setTempPlatform] = useState('');
    const [tempSentiment, setTempSentiment] = useState('');

    useEffect(() => {
        setPage(1);
        fetchComments();
    }, [platformFilter, sentimentFilter]);

    useEffect(() => {
        fetchComments();
    }, [page]);

    useEffect(() => {
        setPage(1);
        fetchComments();
    }, [limit]);

    const fetchComments = async () => {
        try {
            setLoading(true);

            const skip = (page - 1) * limit;
            const params: any = {
                skip,
                limit: limit
            };

            if (searchQuery) params.search_query = searchQuery;
            if (platformFilter) params.platform = platformFilter;
            if (sentimentFilter) params.sentiment = sentimentFilter;

            const response = await getAllComments(params);
            setComments(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch comments', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchComments();
    };

    const openFilterModal = () => {
        setTempPlatform(platformFilter);
        setTempSentiment(sentimentFilter);
        setShowFilterModal(true);
    };

    const getSentimentColor = (sentiment: string) => {
        switch (sentiment) {
            case 'POSITIVE':
                return '#4CAF50';
            case 'NEGATIVE':
                return '#F44336';
            case 'NEUTRAL':
                return '#FFC107';
            default:
                return '#9E9E9E';
        }
    };

    const getSentimentLabel = (sentiment: string) => {
        return sentiment.charAt(0) + sentiment.slice(1).toLowerCase();
    };

    const truncateText = (text: string | null, maxLength: number = 100) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const getCommentUrl = (comment: any) => {
        const permalink = comment.post?.permalink;
        if (!permalink) return '#';

        const platform = comment.post?.platform?.toUpperCase();

        if (platform === 'INSTAGRAM' || platform === 'FACEBOOK') {
            const cleanPermalink = permalink.endsWith('/') ? permalink.slice(0, -1) : permalink;
            return `${cleanPermalink}/c/${comment.commentId}/`;
        }

        return permalink;
    };

    const handleToggleBookmark = async (comment: Comment) => {
        if (!comment) return;
        try {
            const newBookmarkedState = !comment.isBookmarked;

            // Optimistic update for list
            setComments(prev => prev.map(c =>
                c.commentId === comment.commentId ? { ...c, isBookmarked: newBookmarkedState } : c
            ));

            await updateComment(comment.commentId, { isBookmarked: newBookmarkedState } as any);
        } catch (error) {
            console.error('Failed to toggle bookmark', error);
            // Revert on error
            setComments(prev => prev.map(c =>
                c.commentId === comment.commentId ? { ...c, isBookmarked: comment.isBookmarked } : c
            ));
        }
    };

    return (
        <div className="page-container">
            {/* Header */}
            <div className="page-header" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>Comment List</h1>
                    <p className="page-subtitle" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        View and manage social media comments
                    </p>
                </div>
            </div>

            {/* Search and Filters */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                <div className="search-bar" style={{ flex: 1, maxWidth: '500px' }}>
                    <Search size={20} />
                    <input
                        type="text"
                        placeholder="Search Comments"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                </div>

                <button
                    className="btn-outline"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                    }}
                    onClick={openFilterModal}
                >
                    <Filter size={16} />
                    Filter
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="loading-state">Loading comments...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Post Media</th>
                                <th style={{ width: '25%' }}>Post Copy</th>
                                <th style={{ width: '30%' }}>Comment</th>
                                <th style={{ width: '100px' }}>Platform</th>
                                <th style={{ width: '120px' }}>Sentiment</th>
                                <th style={{ width: '50px' }}></th>
                                <th style={{ width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comments.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                        No comments found
                                    </td>
                                </tr>
                            ) : (
                                comments.map((comment) => (
                                    <tr key={comment.commentId}>
                                        {/* Post Media */}
                                        <td>
                                            {comment.post.imageUrl ? (
                                                <img
                                                    src={comment.post.imageUrl}
                                                    alt="Post"
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    backgroundColor: '#1a1a1a',
                                                    borderRadius: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <span style={{ color: 'white', fontSize: '0.75rem' }}>No Image</span>
                                                </div>
                                            )}
                                        </td>

                                        {/* Post Copy */}
                                        <td>
                                            <div style={{ fontSize: '0.875rem' }}>
                                                {truncateText(comment.post.postBody || comment.post.postDescription)}
                                            </div>
                                        </td>

                                        {/* Comment */}
                                        <td>
                                            <div style={{ fontSize: '0.875rem' }}>
                                                {truncateText(comment.commentBody, 120)}
                                            </div>
                                        </td>

                                        {/* Platform */}
                                        <td>
                                            <span
                                                className={`platform-badge platform-${comment.post.platform.toLowerCase()}`}
                                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: 'fit-content' }}
                                            >
                                                {comment.post.platform.toUpperCase() === 'INSTAGRAM' || comment.post.platform.toUpperCase() === 'FACEBOOK' && (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.645.069-4.849.069-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                                    </svg>
                                                )}
                                                {comment.post.platform.toUpperCase() === 'TIKTOK' && (
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                                    </svg>
                                                )}
                                                {comment.post.platform}
                                            </span>
                                        </td>

                                        {/* Sentiment */}
                                        <td>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.375rem 0.75rem',
                                                borderRadius: '6px',
                                                backgroundColor: `${getSentimentColor(comment.aiSentimentEdited || comment.aiSentiment)}15`,
                                                color: getSentimentColor(comment.aiSentimentEdited || comment.aiSentiment),
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                <span style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: getSentimentColor(comment.aiSentimentEdited || comment.aiSentiment)
                                                }} />
                                                {getSentimentLabel(comment.aiSentimentEdited || comment.aiSentiment)}
                                            </span>
                                        </td>

                                        {/* Bookmark */}
                                        <td>
                                            <button
                                                className="icon-btn"
                                                style={{ padding: '0.25rem' }}
                                                onClick={() => handleToggleBookmark(comment)}
                                            >
                                                <Bookmark
                                                    size={18}
                                                    fill={comment.isBookmarked ? 'currentColor' : 'none'}
                                                />
                                            </button>
                                        </td>

                                        {/* Actions */}
                                        <td>
                                            {comment.post.permalink && (
                                                <a
                                                    href={getCommentUrl(comment)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-primary"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        padding: '0.5rem 1rem',
                                                        fontSize: '0.875rem',
                                                        textDecoration: 'none'
                                                    }}
                                                >
                                                    View
                                                    <ExternalLink size={14} />
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {!loading && total > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', flexWrap: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1.5rem', flexShrink: 0 }}>
                                <span style={{ fontSize: '0.875rem', color: '#64748b', whiteSpace: 'nowrap' }}>Rows per page:</span>
                                <select
                                    value={limit}
                                    onChange={(e) => setLimit(Number(e.target.value))}
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        border: '1px solid #e2e8f0',
                                        fontSize: '0.875rem',
                                        color: '#475569',
                                        backgroundColor: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <span style={{ fontSize: '0.875rem', color: '#64748b', marginRight: '1rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results
                            </span>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="icon-btn"
                                    style={{
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                                        opacity: page === 1 ? 0.5 : 1
                                    }}
                                >
                                    <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>‹</span>
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b', padding: '0 0.5rem' }}>
                                        Page {page} of {Math.ceil(total / limit)}
                                    </span>
                                </div>
                                <button
                                    onClick={() => setPage(p => Math.min(Math.ceil(total / limit), p + 1))}
                                    disabled={page >= Math.ceil(total / limit)}
                                    className="icon-btn"
                                    style={{
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0',
                                        cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                                        opacity: page >= Math.ceil(total / limit) ? 0.5 : 1
                                    }}
                                >
                                    <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>›</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Filter Modal */}
            {showFilterModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }} onClick={() => setShowFilterModal(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        width: '500px',
                        maxWidth: '90%',
                        padding: '1.5rem',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Filter Comments</h3>
                            <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                <X size={20} color="#64748b" />
                            </button>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '2rem' }}>
                            Select filters below, this will curate a selective list of customer comments
                        </p>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.75rem', color: '#1e293b' }}>Platform</label>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                {['All', 'TikTok', 'TikTok Ad', 'Instagram', 'Instagram Ad', 'Facebook'].map(p => {
                                    const value = p === 'All' ? '' : p;
                                    const isSelected = tempPlatform === value;
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => setTempPlatform(value)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                borderRadius: '9999px',
                                                border: isSelected ? '1px solid black' : '1px solid #e2e8f0',
                                                backgroundColor: isSelected ? 'black' : 'white',
                                                color: isSelected ? 'white' : '#64748b',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <label style={{ display: 'block', fontWeight: 500, marginBottom: '0.75rem', color: '#1e293b' }}>Sentiment</label>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                {['All', 'Positive', 'Neutral', 'Negative'].map(s => {
                                    const value = s === 'All' ? '' : s.toUpperCase();
                                    const isSelected = tempSentiment === value;
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => setTempSentiment(value)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                borderRadius: '9999px',
                                                border: isSelected ? '1px solid black' : '1px solid #e2e8f0',
                                                backgroundColor: isSelected ? 'black' : 'white',
                                                color: isSelected ? 'white' : '#64748b',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
                            <button
                                onClick={() => {
                                    setTempPlatform('');
                                    setTempSentiment('');
                                }}
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e2e8f0',
                                    backgroundColor: 'white',
                                    color: '#64748b',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                Clear
                            </button>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button
                                    onClick={() => setShowFilterModal(false)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: 'none',
                                        backgroundColor: 'white',
                                        color: '#64748b',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setPlatformFilter(tempPlatform);
                                        setSentimentFilter(tempSentiment);
                                        setShowFilterModal(false);
                                    }}
                                    className="btn-primary"
                                    style={{ padding: '0.5rem 1.5rem' }}
                                >
                                    View Comments
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommentListPage;
